import { ReactNode, useCallback, useMemo, useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { enqueueSnackbar } from '@mint-lib/notifications';

import { SaveTemplateDialog } from '../../components/molecules/SaveTemplateDialog.jsx';
import { UploadFileDialog } from '../../components/organisms/UploadFileDialog.jsx';
import { TABS } from '../../constants/importHubTabs.js';
import { useImportHubV2 } from '../../hooks/ff/index.js';
import { useDataFileSockets } from '../../hooks/index.js';
import {
  useDataFileRouteApi,
  useImportHubPaths,
} from '../../hooks/useImportHubRouteApi.js';
import { useImportOperation } from '../../hooks/useImportOperation.js';
import {
  useDataFilePreview,
  useDataFileServerStepsConfig,
  useUpdateCurrentStep,
} from '../../queries/index.js';
import { DataFileContext } from './DataFileContext.jsx';
import { useSyncDataFileRoute } from './useSyncDataFileRoute.js';

export const DataFileProvider = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation('@myn/import-hub');

  const dataFileRoute = useDataFileRouteApi();
  const paths = useImportHubPaths();
  const { dataFileId } = dataFileRoute.useParams();
  const navigate = dataFileRoute.useNavigate();

  const { data: dataFilePreview } = useDataFilePreview(dataFileId);

  useDataFileSockets({ dataFileId, dataFilePreview });
  useSyncDataFileRoute(dataFileId, dataFilePreview);

  const { currentStep, isEdited } = dataFilePreview;
  const isFromTemplate = Boolean(dataFilePreview.fromTemplate);

  const importOperation = useImportOperation();
  const isImportHubV2 = useImportHubV2();

  const [actionsContainerElement, setActionsContainerElement] =
    useState<HTMLDivElement | null>(null);

  const [isUploadFileDialogOpen, setIsUploadFileDialogOpen] = useState(false);
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);

  const stepsConfig = useDataFileServerStepsConfig(dataFileId);
  const {
    mutate: updateCurrentStep,
    mutateAsync: updateCurrentStepAsync,
    isPending: isUpdatingCurrentStep,
  } = useUpdateCurrentStep(dataFileId);

  const goToPrevStep = useCallback(async () => {
    updateCurrentStep(-1);
  }, [updateCurrentStep]);

  const finishImport = useCallback(
    async (isNewTemplate?: boolean) => {
      importOperation.restartIfEnded();
      try {
        await updateCurrentStepAsync(1);
        importOperation.succeed({
          fromTemplate: isFromTemplate,
          savedAsTemplate: Boolean(isNewTemplate),
          importHubV2: isImportHubV2,
        });
        navigate({
          to: paths.performance,
          search: { tab: isNewTemplate ? TABS.templates : TABS.imports },
        });
        enqueueSnackbar({
          title: t('We are processing the file'),
          subTitle: t(
            'The data is going to be visible in Reporting as soon as possible.',
          ),
          variant: 'success',
          removable: true,
        });
      } catch {
        importOperation.fail();
      }
    },
    [
      t,
      navigate,
      paths.performance,
      updateCurrentStepAsync,
      importOperation,
      isFromTemplate,
      isImportHubV2,
    ],
  );

  const goToNextStep = useCallback(async () => {
    const isLastStep = stepsConfig.isLastStep(currentStep);

    if (isEdited) {
      updateCurrentStep(1);
      return;
    }

    if (isLastStep) {
      /* finish import */
      if (dataFilePreview.fromTemplate) {
        finishImport(false);
      } else {
        setIsTemplateDialogOpen(true);
      }
      return;
    }

    updateCurrentStep(1);
  }, [
    updateCurrentStep,
    stepsConfig,
    isEdited,
    dataFilePreview,
    currentStep,
    finishImport,
  ]);

  const showUploadFileDialog = useCallback(() => {
    setIsUploadFileDialogOpen(true);
  }, []);

  const hideUploadFileDialog = useCallback(() => {
    setIsUploadFileDialogOpen(false);
  }, []);

  const value = useMemo(
    () => ({
      dataFileId,
      dataFilePreview,
      actionsContainerElement,
      setActionsContainerElement,
      goToPrevStep,
      goToNextStep,
      isSubmitting: isUpdatingCurrentStep,
      showUploadFileDialog,
      hideUploadFileDialog,
    }),
    [
      dataFileId,
      dataFilePreview,
      actionsContainerElement,
      setActionsContainerElement,
      goToPrevStep,
      goToNextStep,
      isUpdatingCurrentStep,
      showUploadFileDialog,
      hideUploadFileDialog,
    ],
  );

  return (
    <DataFileContext.Provider value={value}>
      {children}
      {isUploadFileDialogOpen && (
        <UploadFileDialog
          onClose={hideUploadFileDialog}
          dataFileId={dataFileId}
        />
      )}
      {isTemplateDialogOpen && (
        <SaveTemplateDialog
          open
          onSubmit={finishImport}
          onSubmitStart={importOperation.restartIfEnded}
          onSubmitError={importOperation.fail}
          onClose={() => setIsTemplateDialogOpen(false)}
        />
      )}
    </DataFileContext.Provider>
  );
};
